import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type Dispatch,
  type KeyboardEvent,
  type MouseEvent,
  type SetStateAction,
} from 'react'
import { Link } from 'react-router-dom'
import { runtimeConfig } from '@/config/runtimeConfig'
import { useAppNavigation } from '@features/navigation/hooks/useAppNavigation'
import { useRouteNavigate } from '@features/navigation/hooks/useRouteNavigate'
import { openGlobalSearch } from '@features/search/logic/openGlobalSearch'
import { ShortcutHint } from '@features/shared/components/ShortcutHint'
import { SearchIcon, type IconProps } from '@features/shared/icons/icons'
import { createLayoutResizeObserver } from '@features/shared/utils/layoutResizeObserver'
import { useSidebarSearch } from '../hooks/useSidebarSearch'
import { measureRailAnchor, RAIL_MODULE_ATTRIBUTE } from '../logic/railAnchor'
import type { SidebarSearchResult } from '../logic/sidebarSearch'
import { SEARCH_RAIL_ITEM } from '../models'
import { FavoriteToggle } from './FavoriteToggle'
import { FavoriteTrail } from './FavoritesMenu'
import MenuScrollArea from './MenuScrollArea'
import { RAIL_FLYOUT_CARD_CLASS, RAIL_FLYOUT_LEFT_CLASS } from './ModuleFlyout'
import { MENU_ICON_SLOTS_CLASS, MENU_ROW_END_PAD_CLASS } from './menuIconSlots'
import { RAIL_NAME_CARD_ATTRIBUTE } from './RailNameCard'
import { TableListSearch } from './TableListControls'

// A result's icon, its area's, as on a favorite's row.
const RESULT_ICON_SIZE = 15
const RESULTS_ID = 'sidebar-search-results'
const resultId = (index: number) => `sidebar-search-result-${index}`
const SEARCH_ITEM_SELECTOR = `[${RAIL_MODULE_ATTRIBUTE}="${SEARCH_RAIL_ITEM}"]`

type ResultIcon = ComponentType<IconProps>

interface Placement {
  top: number
  maxHeight: number
}

function SearchResultRow({
  result,
  index,
  icon: Icon,
  isActive,
  onActivate,
  onChoose,
}: {
  result: SidebarSearchResult
  index: number
  icon: ResultIcon
  isActive: boolean
  onActivate: () => void
  onChoose: () => void
}) {
  return (
    <div
      id={resultId(index)}
      role="option"
      aria-selected={isActive}
      onMouseMove={onActivate}
      className={`group relative flex h-[var(--sidebar-tile-size)] w-full min-w-0 items-center rounded-md pl-2 ${MENU_ROW_END_PAD_CLASS} text-sm text-[color:var(--sidebar-submenu-text-data)] ${
        // The keyboard's row and the pointer's are one, lit like a hovered row.
        isActive
          ? 'bg-[color:var(--sidebar-item-hover-bg)]'
          : 'hover:bg-[color:var(--sidebar-item-hover-bg)]'
      }`}
    >
      <Link
        to={result.path}
        tabIndex={-1}
        // The full text of the truncated trail, on the link (whose overlay covers
        // the row) so the heart shows only its own tooltip.
        title={result.parts.join(' › ')}
        className="flex min-w-0 flex-1 items-center gap-2 self-stretch text-left after:absolute after:inset-0 after:rounded-md after:content-['']"
        onClick={(event) => {
          event.stopPropagation()
          if (!event.ctrlKey && !event.metaKey) onChoose()
        }}
      >
        <Icon size={RESULT_ICON_SIZE} className="shrink-0" aria-hidden="true" />
        <FavoriteTrail parts={result.parts} isActive={false} />
      </Link>
      {/* Its heart, in the last slot as on a menu's rows (menuIconSlots). */}
      <span
        className={`pointer-events-none relative z-10 ml-auto h-6 pl-2 ${MENU_ICON_SLOTS_CLASS}`}
      >
        <span className="pointer-events-auto flex">
          <FavoriteToggle path={result.path} variant="row" />
        </span>
      </span>
    </div>
  )
}

/**
 * The sidebar's search, beside the rail, as a pinned menu card is: from the
 * page card's top to its bottom. Before anything is typed it lists the places
 * the user opened lately, newest first (Recent), then every other page and
 * table A to Z (All Pages), to browse; typing filters at once to anything
 * they could favorite (their pages, tables and views; useSidebarSearch), best
 * match first in one flat list, each read as a favorite is: its area's icon
 * and its trail (Tables › Accounts › All Records), with its heart. Arrow keys
 * move through the list and Enter opens one. With nothing found, Enter (or
 * the button) hands the text to the header's search of everything. Shown on a
 * rest, it closes as the pointer leaves, as a menu does; typed or clicked
 * into, it's held: Escape clears the box, then closes it, as does a click
 * outside or choosing one.
 */
function SidebarSearchCard({
  query,
  onQueryChange,
  search,
  held,
  onHold,
  onEnter,
  onLeave,
  onClose,
}: {
  query: string
  onQueryChange: Dispatch<SetStateAction<string>>
  search: ReturnType<typeof useSidebarSearch>
  held: boolean
  onHold: () => void
  onEnter: () => void
  onLeave: () => void
  onClose: () => void
}) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLElement>(null)
  const [placement, setPlacement] = useState<Placement | null>(null)
  // The highlighted row, for the text it was picked under: a new search starts at the top.
  const [active, setActive] = useState({ term: '', index: 0 })
  const { results: found, recent, alphabetical, isSearching } = search
  // Keep this opening's initial rows in place. Recent-page descriptions,
  // navigation metadata, and background refreshes must not move a target
  // after the first paint. Typing uses live matches; reopening gets fresh rows.
  const [browse] = useState(() => ({ recent, alphabetical }))
  const { modules } = useAppNavigation()
  const navigate = useRouteNavigate()
  const term = query.trim()
  // Nothing typed yet: the places opened lately, then everything else A to Z,
  // each under its heading; typed, one list best first.
  const sections: { title: string | null; results: SidebarSearchResult[] }[] = term
    ? [{ title: null, results: found }]
    : [
        { title: 'Recent', results: browse.recent },
        { title: 'All Pages', results: browse.alphabetical },
      ].filter((section) => section.results.length > 0)
  const results = sections.flatMap((section) => section.results)
  const activeIndex =
    active.term === term ? Math.min(active.index, Math.max(0, results.length - 1)) : 0
  const shortcutModifierLabel = runtimeConfig.environment.shortcutModifierLabel

  // Placed before the browser paints, so it never shows in the wrong spot.
  useLayoutEffect(() => {
    const layer = wrapperRef.current?.offsetParent
    if (!(layer instanceof HTMLElement)) return
    const place = () => {
      const { top, room } = measureRailAnchor(layer, SEARCH_RAIL_ITEM)
      setPlacement((current) =>
        current?.top === top && current.maxHeight === room ? current : { top, maxHeight: room }
      )
    }
    place()
    const observer = createLayoutResizeObserver(place)
    observer.observe(layer)
    return () => observer.disconnect()
  }, [])

  // A click anywhere else closes it; the Search item toggles it itself.
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (cardRef.current?.contains(target) || target.closest(SEARCH_ITEM_SELECTOR)) return
      onClose()
    }
    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [onClose])

  useEffect(() => {
    document.getElementById(resultId(activeIndex))?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const input = () => cardRef.current?.querySelector<HTMLInputElement>('input[type="search"]')
  // Held (Search clicked while it showed), the cursor goes into its box.
  useEffect(() => {
    if (held) input()?.focus()
  }, [held])

  // Shown on a rest, typing goes into its box, as over the Tables menu, and
  // holds it open: but never keys from a field, or from the page (the table
  // grid reads them too).
  useEffect(() => {
    if (held) return
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return
      if (event.isComposing || event.key.length !== 1 || event.key === ' ') return
      const focused = document.activeElement
      if (focused && focused !== document.body && !focused.closest('[data-app-sidebar]')) return
      if (focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement) return
      event.preventDefault()
      onQueryChange((current) => current + event.key)
      onHold()
      input()?.focus()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [held, onHold, onQueryChange])

  // Shown on a rest, it closes as the pointer leaves it, but for Search.
  const handleMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
    if (held) return
    const to = event.relatedTarget
    if (to instanceof Element && to.closest(SEARCH_ITEM_SELECTOR)) return
    onLeave()
  }

  const closeToRail = () => {
    onClose()
    document.querySelector<HTMLElement>(SEARCH_ITEM_SELECTOR)?.focus()
  }
  const choose = (result: SidebarSearchResult) => {
    onClose()
    navigate(result.path)
  }
  const searchEverything = () => {
    onClose()
    openGlobalSearch(term)
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (results.length === 0) return
      event.preventDefault()
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive({ term, index: (activeIndex + step + results.length) % results.length })
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const result = results[activeIndex]
      if (result) choose(result)
      else if (term && !isSearching) searchEverything()
    } else if (event.key === 'Escape' && !query) {
      // With text in the box, Escape clears it first (TableListSearch).
      event.preventDefault()
      closeToRail()
    }
  }

  const iconFor = (result: SidebarSearchResult): ResultIcon =>
    modules.find((module) => module.pathPrefix === `/${result.area}`)?.icon ?? SearchIcon

  let body
  if (results.length > 0) {
    body = (
      <MenuScrollArea className="flex-auto">
        <div id={RESULTS_ID} role="listbox" aria-label={term ? 'Pages found' : 'Pages'}>
          {sections.map((section, sectionIndex) => {
            // Each row's place in the whole list, for the keyboard.
            const start = sections
              .slice(0, sectionIndex)
              .reduce((count, earlier) => count + earlier.results.length, 0)
            return (
              <div
                key={section.title ?? 'found'}
                role="group"
                aria-label={section.title ?? undefined}
                className="space-y-[var(--sidebar-item-inset)]"
              >
                {section.title && (
                  <div
                    aria-hidden="true"
                    className={`px-2 pb-1 text-xs font-medium text-[color:var(--sidebar-submenu-text-faint)] ${
                      sectionIndex === 0 ? 'pt-1' : 'pt-3'
                    }`}
                  >
                    {section.title}
                  </div>
                )}
                {section.results.map((result, offset) => {
                  const index = start + offset
                  return (
                    <SearchResultRow
                      key={result.key}
                      result={result}
                      index={index}
                      icon={iconFor(result)}
                      isActive={index === activeIndex}
                      onActivate={() => {
                        if (index !== activeIndex) setActive({ term, index })
                      }}
                      onChoose={onClose}
                    />
                  )
                })}
              </div>
            )
          })}
        </div>
      </MenuScrollArea>
    )
  } else if (!term) {
    body = (
      <p className="px-3 py-2 text-sm text-[color:var(--sidebar-submenu-text-faint)]">
        Find any page, table or view.
      </p>
    )
  } else if (isSearching) {
    body = (
      <p className="px-3 py-2 text-sm text-[color:var(--sidebar-submenu-text-faint)]">Searching…</p>
    )
  } else {
    body = (
      <div className="flex flex-col gap-[var(--sidebar-item-inset)] p-[var(--sidebar-item-inset)]">
        <p className="px-2 py-1 text-sm text-[color:var(--sidebar-submenu-text-faint)]">
          No pages, tables or views match.
        </p>
        {/* The bridge to the header's search, for a record or a value. */}
        <button
          type="button"
          onClick={searchEverything}
          className="flex h-[var(--sidebar-tile-size)] w-full min-w-0 items-center gap-2 rounded-md px-2 text-left text-sm text-[color:var(--sidebar-submenu-text-data)] hover:bg-[color:var(--sidebar-item-hover-bg)]"
        >
          <SearchIcon size={RESULT_ICON_SIZE} className="shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">Search everything</span>
          <ShortcutHint
            keys={[shortcutModifierLabel, 'K']}
            className="border-[color:var(--sidebar-submenu-border)] text-[color:var(--sidebar-submenu-text-faint)]"
          />
        </button>
      </div>
    )
  }

  return (
    <div
      ref={wrapperRef}
      // Search's own card: the pointer crossing to it from Search keeps it.
      {...{ [RAIL_NAME_CARD_ATTRIBUTE]: '' }}
      // Search responds with the rail, without another fade after hover intent.
      data-shown="true"
      className={`rail-flyout-fade rail-flyout-instant absolute ${RAIL_FLYOUT_LEFT_CLASS} z-20`}
      style={{ top: placement?.top ?? 0 }}
      onMouseEnter={onEnter}
      onMouseLeave={handleMouseLeave}
    >
      <section
        ref={cardRef}
        // The rail reads where an open menu is, for the pointer's aim (menuAim.ts).
        data-rail-menu="search"
        role="dialog"
        aria-label="Search pages"
        className={`sidebar-search flex flex-col overflow-hidden text-[color:var(--sidebar-submenu-text-subtle)] ${RAIL_FLYOUT_CARD_CLASS}`}
        // The page card's whole height, as a pinned menu, however few its rows.
        style={{ width: 'var(--sidebar-menu-width)', height: placement?.maxHeight }}
        onKeyDown={(event) => {
          // Escape from anywhere in it (a result's heart) closes it.
          if (event.key !== 'Escape' || event.defaultPrevented) return
          event.preventDefault()
          closeToRail()
        }}
      >
        <TableListSearch
          label="Pages"
          value={query}
          onChange={onQueryChange}
          // Clicked open, the cursor's in it; shown on a rest, it waits.
          autoFocus={held}
          inputProps={{
            // Into its box, it's held: it stays when the pointer leaves.
            onFocus: onHold,
            role: 'combobox',
            'aria-expanded': results.length > 0,
            'aria-controls': RESULTS_ID,
            'aria-autocomplete': 'list',
            'aria-activedescendant': results.length > 0 ? resultId(activeIndex) : undefined,
            onKeyDown: handleInputKeyDown,
          }}
        />
        {body}
      </section>
    </div>
  )
}

/**
 * The sidebar's search card while it shows (SidebarSearchCard), a fresh
 * search each time: on a rest on Search or the card itself, as an area's
 * menu, without taking the cursor; `held` (Search clicked, or the card typed
 * or clicked into) it stays until closed.
 */
export default function SidebarSearch({
  open,
  held,
  onHold,
  onEnter,
  onLeave,
  onClose,
}: {
  open: boolean
  held: boolean
  onHold: () => void
  /** The pointer came onto the card: a leave on its way is called off. */
  onEnter: () => void
  onLeave: () => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  // Stay subscribed while closed, so recent-page names and the browse list
  // are ready before hover mounts the card rather than arriving a paint later.
  const search = useSidebarSearch(query)
  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  return open ? (
    <SidebarSearchCard
      query={query}
      onQueryChange={setQuery}
      search={search}
      held={held}
      onHold={onHold}
      onEnter={onEnter}
      onLeave={onLeave}
      onClose={onClose}
    />
  ) : null
}
